<template>
  <div class="custom-collab-details-page">
    <Loader v-if="isLoading" />

    <template v-else>
      <h1 class="global-h1 custom-collab-details-page__title">
        {{ customCollabClientPayload?.customerCollabInputs?.title ?? `Custom Collab with ${customCollabClientPayload?.customer.title}` }}
      </h1>

      <div class="custom-collab-details-page__divider"></div>

      <!-- Community logo snippet -->
      <div class="community-logo-snippet custom-collab-details-page__community-logo-snippet">
        <div class="community-logo-snippet__logo-wrap">
          <img class="community-logo-snippet__logo" :src="`${contentBaseUri}/cms/images/orgs/${customCollabClientPayload.customer.icon}`" alt="Funtown" />
        </div>
        <div class="global-h3 community-logo-snippet__name">
          {{ customCollabClientPayload.customer.title }}
        </div>
      </div>
      <!-- / Community logo snippet -->

      <div class="custom-collab-details-page__content">
        <div class="custom-collab-details-page__content-left-col">
          <div
            class="custom-collab-details-page__content-left-col-in-wrap"
            ref="domRefMsgThreadContainer"
            :style="
              ['mobile', 'tablet'].includes(screenSize)
                ? null
                : {
                    height: `calc(100dvh - ${msgThreadContainerViewportTopIndent >= 0 ? msgThreadContainerViewportTopIndent : 0}px)`,
                    minHeight: `calc(100dvh - ${msgThreadContainerViewportTopIndent >= 0 ? msgThreadContainerViewportTopIndent : 0}px)`,
                    maxHeight: `calc(100dvh - ${msgThreadContainerViewportTopIndent >= 0 ? msgThreadContainerViewportTopIndent : 0}px)`,
                  }
            "
          >
            <div v-if="['mobile', 'tablet'].includes(screenSize)" class="custom-collab-details-page__white-snippet-wrap">
              <SrpDetailsSummary isOpened>
                <template #heading>
                  <h2 class="global-h2">
                    <span><span>Messages</span></span>
                  </h2>
                </template>

                <template #details>
                  <CollabMessagesThread
                    class="custom-collab-details-page__messages-thread"
                    ref="domRefCollabMessagesThread"
                    :isImpersonating="isImpersonating"
                    :isEscalateContextVisible="false"
                    :isAutoFocusTextarea="false"
                    :isMsgBubblesFullWidth="true"
                    :messageThreadId="customCollabClientPayload.customerCollabInputs.messageThreadId"
                    :customerId="customCollabClientPayload.customerCollabInputs.customerId"
                    :creatorDetails="null"
                    messageSenderType="CreatorId"
                    :customCollabId="customCollabClientPayload.customerCollabInputs.id"
                    :hideShrpaName="true"
                  />
                </template>
              </SrpDetailsSummary>
            </div>

            <div v-else class="custom-collab-details-page__white-snippet-wrap" style="height: 100%; padding: 0 0 15px; box-sizing: border-box; display: flex; flex-direction: column">
              <h2 class="global-h2" style="margin: 20px 24px 20px">Messages</h2>

              <div class="custom-collab-details-page__messages-thread-wrap">
                <CollabMessagesThread
                  class="custom-collab-details-page__messages-thread"
                  ref="domRefCollabMessagesThread"
                  :isImpersonating="isImpersonating"
                  :isEscalateContextVisible="false"
                  :isAutoFocusTextarea="false"
                  :isMsgBubblesFullWidth="true"
                  :messageThreadId="customCollabClientPayload.customerCollabInputs.messageThreadId"
                  :customerId="customCollabClientPayload.customerCollabInputs.customerId"
                  :creatorDetails="null"
                  messageSenderType="CreatorId"
                  :customCollabId="customCollabClientPayload.customerCollabInputs.id"
                  :hideShrpaName="true"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="custom-collab-details-page__content-right-col">
          <WorkStatusSnippet
            class="custom-collab-details-page__work-status-snippet"
            :customCollabClientPayload="customCollabClientPayload"
            @save="saveCreatorInputs"
            :isSaving="isSaving"
            @showSendForCreatorAcceptance="showSendForCreatorAcceptanceModalClicked"
            @click:deliverablesAreReady="deliverablesAreReadyClicked"
          />

          <CollabDetailsInfo class="custom-collab-details-page__collab-details-snippet" :customCollabClientPayload="customCollabClientPayload" :isOpened="hasCollabInputsSet" />

          <HowItWorks class="custom-collab-details-page__how-it-works-snippet" :isOpened="!hasCollabInputsSet" />
        </div>
      </div>

      <div style="margin-top: 30px; display: flex; justify-content: flex-end; width: 100%">
        <SuperUserActionPanel>
          <div>
            <router-link
              v-if="customCollabClientPayload.customerCollabInputs.creatorId"
              :to="{
                name: 'CustomerCustomCollabDetails',
                params: {
                  pageId: customCollabClientPayload.customerCollabInputs.customerId,
                  customCollabId: customCollabClientPayload.customerCollabInputs.id,
                  creatorId: customCollabClientPayload.customerCollabInputs.creatorId,
                },
              }"
              style="margin-right: 12px"
            >
              <SrpButton color="gray" fill="outlined" size="tiny" style="opacity: 0.5">Customer View</SrpButton>
            </router-link>
          </div>
          <div style="margin-top: 10px">
            <MessagingImpersonateCheckbox v-model:isImpersonating="isImpersonating" />
          </div>
        </SuperUserActionPanel>
      </div>

      <!-- Modals -->
      <SrpModal :isVisible="showSendForCreatorAcceptanceModal" @close="showSendForCreatorAcceptanceModal = false">
        <template #header>
          <h1>Ready to Accept the Work Agreement?</h1>
        </template>
        <template #content>
          <p>
            The destination has requested a formal work agreement.<br />
            <b>By accepting, you agree to complete the work for the destination with the specified terms.</b>
          </p>
          <h3>What's Next?</h3>
          <ul>
            <li>Finalize any details or coordination.</li>
            <li>If any changes are needed, communicate early and often.</li>
            <li>Complete the work and send to the destination.</li>
          </ul>
        </template>
        <template #footer>
          <SrpButton :isDisabled="isSaving" @click="confirmAcceptance" style="margin-right: 10px">I Agree</SrpButton>
          <SrpButton :isDisabled="isSaving" fill="outlined" @click="showSendForCreatorAcceptanceModal = false">Cancel</SrpButton>
        </template>
      </SrpModal>

      <SrpModal :isVisible="showDeliverablesAreReadyModal" @close="showDeliverablesAreReadyModal = false">
        <template #header>
          <h1>Mark Deliverables as Ready for Review</h1>
        </template>
        <template #content>
          <p>
            This will notify the destination that the deliverables are ready for review.<br />
            If they have feedback or questions, they will be able to message you.
          </p>
        </template>
        <template #footer>
          <SrpButton :isDisabled="isSaving" @click="confirmDeliverablesAreReady" style="margin-right: 10px">Notify Destination</SrpButton>
          <SrpButton :isDisabled="isSaving" fill="outlined" @click="showDeliverablesAreReadyModal = false">Not yet</SrpButton>
        </template>
      </SrpModal>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, inject, Ref } from "vue";
import { useRoute } from "vue-router";
import CreatorCustomCollabsRepo from "@repos/CreatorCustomCollabsRepo";
import { hasAnyCustomerDetailsAdded } from "@/helpers/CustomCollabsHelper";
import { useHead } from "@unhead/vue";
import { MetricSender } from "@/helpers/MetricSender";

// Components
import WorkStatusSnippet from "./WorkStatusSnippet.vue";
import HowItWorks from "./HowItWorks.vue";
import CollabMessagesThread from "@views/PaidCollab/CollabMessagesThread/index.vue";
import Loader from "@components/Loader/Loader.vue";
import CollabDetailsInfo from "./CollabDetailsInfo.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import MessagingImpersonateCheckbox from "@/components/MessagingImpersonateCheckbox.vue";

// Types
import { CustomCollabClientPayload } from "@contracts/customCollabs";
import { ScreenSize } from "@contracts/screenSize";

// Global variables & injections
const contentBaseUri = globalThis.Bootstrap.Config.contentCdnBaseUri;
const screenSize = inject("screenSize") as Ref<ScreenSize>;

// Stores
import { useUserProfileStore } from "@/stores/userProfileStore";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
const userProfileStore = useUserProfileStore();

// Composables
import { useMsgThreadTopIndentDetection } from "@views/CustomCollabs/useMsgThreadTopIndentDetection";

// Router
const route = useRoute();

// Load data ==================================================================
const isLoading = ref<boolean>(true);
const customCollabClientPayload = ref<CustomCollabClientPayload | null>(null);
const hasCollabInputsSet = ref<boolean>(false);

async function loadData() {
  isLoading.value = true;
  customCollabClientPayload.value = await CreatorCustomCollabsRepo.getCustomCollab(String(route.params.creatorId), String(route.params.communityId), String(route.params.customCollabId));
  isLoading.value = false;
}

onMounted(async () => {
  await userProfileStore.impersonateProfile(String(route.params.creatorId));

  await loadData();

  if (hasAnyCustomerDetailsAdded(customCollabClientPayload.value?.customerCollabInputs, false)) {
    hasCollabInputsSet.value = true;
  }

  MetricSender.sendMetric("Creator-ViewCustomCollabDetails", null);
});

// Save creator inputs ========================================================
const isSaving = ref<boolean>(false);

async function saveCreatorInputs() {
  isSaving.value = true;

  // Remove empty deliverable fields
  const filledDeliverables = customCollabClientPayload.value.creatorCollabInputs.deliverables.filter(d => Boolean(d));
  customCollabClientPayload.value.creatorCollabInputs.deliverables = filledDeliverables;

  customCollabClientPayload.value = await CreatorCustomCollabsRepo.saveCustomCollab(
    String(route.params.creatorId),
    String(route.params.communityId),
    String(route.params.customCollabId),
    customCollabClientPayload.value.creatorCollabInputs,
    isImpersonating.value
  );

  // Have messages reload in case an update caused a message to be added
  refreshMessages();

  isSaving.value = false;
}

const domRefCollabMessagesThread = ref(null);
function refreshMessages() {
  domRefCollabMessagesThread.value?.loadMessages(true, true);
}

// Accept Collab Details =====================================================
const showSendForCreatorAcceptanceModal = ref<boolean>(false);

function showSendForCreatorAcceptanceModalClicked() {
  showSendForCreatorAcceptanceModal.value = true;
}

async function confirmAcceptance() {
  isSaving.value = true;
  customCollabClientPayload.value = await CreatorCustomCollabsRepo.agreeToCollabDetails(String(route.params.creatorId), String(route.params.communityId), String(route.params.customCollabId));
  isSaving.value = false;
  showSendForCreatorAcceptanceModal.value = false;
}

// Deliverables are ready =====================================================
const showDeliverablesAreReadyModal = ref<boolean>(false);

async function deliverablesAreReadyClicked() {
  showDeliverablesAreReadyModal.value = true;
}

async function confirmDeliverablesAreReady() {
  // Save in case there were any updates
  await saveCreatorInputs();
  isSaving.value = true;
  customCollabClientPayload.value = await CreatorCustomCollabsRepo.markDeliverablesReady(String(route.params.creatorId), String(route.params.communityId), String(route.params.customCollabId));
  isSaving.value = false;
  showDeliverablesAreReadyModal.value = false;
}

// Impersonate toggle =========================================================
const isImpersonating = ref<boolean>(false);

// Set browser title ==========================================================
onMounted(() => {
  useHead({ title: () => `Custom Collab - ${customCollabClientPayload.value?.customerCollabInputs?.title ?? userProfileStore.getViewingUserProfile?.firstName}` });
});

// Get msgThread container viewport top indent ================================
const domRefMsgThreadContainer = ref<HTMLElement | null>(null);

const { msgThreadContainerViewportTopIndent } = useMsgThreadTopIndentDetection(domRefMsgThreadContainer, isLoading);
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Community logo snippet =====================================================
.community-logo-snippet {
  display: flex;
  align-items: center;

  &__logo-wrap {
    width: 70px;
    height: 50px;
    margin-right: 8px;
    display: flex;
    justify-items: center;
    align-items: center;
  }

  &__logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__name {
  }
}

// Custom collab details page =================================================
.custom-collab-details-page {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__title {
    margin-bottom: 27px;
  }

  &__divider {
    margin-bottom: 20px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__community-logo-snippet {
    margin-bottom: 20px;
  }

  &__content {
    min-height: 700px;
    gap: $desktop-wide-grid-gap-width;
    padding: 0;
    display: flex;
    position: relative;
    z-index: 0;
  }

  &__content-left-col {
    width: calc($desktop-wide-grid-col-width * 3 + $desktop-wide-grid-gap-width * 2);
    min-width: calc($desktop-wide-grid-col-width * 3 + $desktop-wide-grid-gap-width * 2);
    align-self: stretch;
    gap: $desktop-wide-grid-gap-width;
    display: flex;
    flex-direction: column;
    position: relative;
  }

  &__content-left-col-in-wrap {
    width: 100%;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    position: sticky;
    inset: 0 auto auto 0;
  }

  &__content-right-col {
    flex-grow: 1;
    gap: $desktop-wide-grid-gap-width;
    display: flex;
    flex-direction: column;
  }

  &__messages-thread-wrap {
    flex-grow: 1;
    padding: 0 20px;
    display: flex;
    flex-direction: column;
  }

  &__messages-thread {
    flex-grow: 1;
    min-height: 350px;
  }

  &__work-status-snippet {
  }

  &__collab-details-snippet {
  }

  &__how-it-works-snippet {
  }

  &__white-snippet-wrap {
    width: 100%;
    padding: 20px 25px 24px;
    box-sizing: border-box;
    border-radius: 6px;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 3px 15px -5px rgba(0, 0, 0, 0.4);
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .custom-collab-details-page {
    width: $desktop-right-col-width;

    &__content {
      gap: $desktop-grid-gap-width;
    }

    &__content-left-col {
      width: calc($desktop-grid-col-width * 3 + $desktop-grid-gap-width * 2);
      min-width: calc($desktop-grid-col-width * 3 + $desktop-grid-gap-width * 2);
      gap: $desktop-grid-gap-width;
    }

    &__content-right-col {
      gap: $desktop-grid-gap-width;
    }
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .custom-collab-details-page {
    width: $laptop-right-col-width;

    &__content {
      gap: $laptop-grid-gap-width;
    }

    &__content-left-col {
      width: calc($laptop-grid-col-width * 4 + $laptop-grid-gap-width * 3);
      min-width: calc($laptop-grid-col-width * 4 + $laptop-grid-gap-width * 3);
      gap: $laptop-grid-gap-width;
    }

    &__content-right-col {
      gap: $laptop-grid-gap-width;
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .custom-collab-details-page {
    width: $tablet-large-content-width;

    &__content {
      gap: $tablet-large-grid-gap-width;
    }

    &__content-left-col {
      width: calc($tablet-large-grid-col-width * 4 + $tablet-large-grid-gap-width * 3);
      min-width: calc($tablet-large-grid-col-width * 4 + $tablet-large-grid-gap-width * 3);
      gap: $tablet-large-grid-gap-width;
    }

    &__content-right-col {
      gap: $tablet-large-grid-gap-width;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .custom-collab-details-page {
    width: $tablet-content-width;

    &__content {
      gap: $tablet-grid-gap-width;
      flex-direction: column;
    }

    &__content-left-col {
      width: 100%;
      gap: $tablet-grid-gap-width;
    }

    &__content-left-col-in-wrap {
      height: auto;
    }

    &__messages-thread {
      min-height: 500px;
    }

    &__content-right-col {
      width: 100%;
      gap: $tablet-grid-gap-width;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .custom-collab-details-page {
    width: $mobile-content-width;

    &__content {
      flex-direction: column;
    }

    &__content-left-col {
      width: 100%;
      min-width: 100%;
    }

    &__content-left-col-in-wrap {
      height: auto;
    }

    &__messages-thread {
      min-height: 500px;
    }

    &__content-right-col {
      gap: $mobile-grid-gap-width;
    }
  }
}
</style>
